import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signUpEmailPassword, signInWithGoogle, signInWithMicrosoft, isFirebaseEnabled } from '@/auth/firebase';

const SITE_URL = (import.meta.env.VITE_SITE_URL ?? (import.meta.env.DEV ? 'http://localhost:5181' : '')).replace(/\/$/, '');

export default function SignUpPage() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const plan = search.get('plan') ?? 'growth';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const cred = await signUpEmailPassword(email, password);
      if (name && cred.user) {
        const { updateProfile } = await import('firebase/auth');
        await updateProfile(cred.user, { displayName: name });
      }
      nav('/app/dashboard', { replace: true });
    } catch (e: any) {
      setErr(e?.message ?? 'Sign-up failed');
    } finally { setBusy(false); }
  }

  async function social(p: 'google' | 'microsoft') {
    setErr(null);
    try {
      if (p === 'google') await signInWithGoogle(); else await signInWithMicrosoft();
      nav('/app/dashboard', { replace: true });
    } catch (e: any) { setErr(e?.message ?? 'Sign-up failed'); }
  }

  const credits = plan === 'starter' ? '500' : plan === 'growth' ? '2,500' : '10,000';

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-violet-50">
      <header className="px-6 py-5">
        <a href={SITE_URL} className="flex items-center gap-2 text-stone-700 hover:text-stone-900">
          <span className="w-7 h-7 bg-violet-600 rounded-md flex items-center justify-center text-white text-sm">◈</span>
          <span className="font-semibold">ContentHub</span>
        </a>
      </header>
      <main className="px-6 pt-8">
        <div className="max-w-md mx-auto">
          <h1 className="text-2xl font-semibold text-stone-900 text-center">Start your free trial</h1>
          <p className="text-sm text-stone-600 text-center mt-1">14 days. {credits} AI credits. No card required.</p>
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm mt-6">
            {!isFirebaseEnabled && (
              <div className="mb-4 p-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded">
                Firebase is not configured. Demo mode — any credentials let you in.
              </div>
            )}

            <div className="space-y-2 mb-5">
              <button onClick={() => social('google')} className="w-full px-4 py-2.5 border border-stone-300 rounded-lg hover:bg-stone-50 text-sm font-medium flex items-center justify-center gap-2">
                <span className="font-bold">G</span> Continue with Google
              </button>
              <button onClick={() => social('microsoft')} className="w-full px-4 py-2.5 border border-stone-300 rounded-lg hover:bg-stone-50 text-sm font-medium flex items-center justify-center gap-2">
                <span className="font-bold">⊞</span> Continue with Microsoft
              </button>
            </div>

            <div className="flex items-center gap-3 my-5 text-xs text-stone-400 uppercase tracking-wider">
              <span className="flex-1 h-px bg-stone-200" /> or <span className="flex-1 h-px bg-stone-200" />
            </div>

            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Full name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Work email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Password</label>
                <input type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                <p className="text-[11px] text-stone-500 mt-1">8+ characters.</p>
              </div>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <button type="submit" disabled={busy} className="w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-lg text-sm font-medium">
                {busy ? '…' : 'Create my account'}
              </button>
              <p className="text-[11px] text-stone-500 text-center">
                By continuing you agree to our{' '}
                <a className="underline hover:text-stone-700" href={`${SITE_URL}/terms`} target="_blank" rel="noreferrer">Terms</a>{' '}
                and{' '}
                <a className="underline hover:text-stone-700" href={`${SITE_URL}/privacy`} target="_blank" rel="noreferrer">Privacy Policy</a>.
              </p>
            </form>
          </div>

          <p className="text-center text-xs text-stone-500 mt-6">
            Already have an account? <Link to="/auth/sign-in" className="text-violet-700 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
